import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import type { FormHelperTextProps, InputBaseComponentProps, TextFieldProps as MuiTextFieldProps, OutlinedInputProps } from '@mui/material';
import { IconButton, InputAdornment, TextField as MuiTextField, Tooltip } from '@mui/material';
import type { FocusEvent, ReactNode, Ref } from 'react';
import { useState } from 'react';

export type TruentityTextFieldProps = MuiTextFieldProps & {
  label?: string;
  min?: number | string | undefined;
  max?: number | string | undefined;
  step?: number | string | undefined;
  maxLength?: number | string | undefined;
  shrink?: boolean;
  icon?: ReactNode;
  multiple?: boolean;
  startIcon?: ReactNode;
  autoFocus?: boolean;
  masked?: boolean;
  maskedVisibleChars?: number;
  ref?: Ref<HTMLDivElement>;
  InputProps?: Partial<OutlinedInputProps>;
  inputProps?: InputBaseComponentProps;
  FormHelperTextProps?: Partial<FormHelperTextProps>;
};

type ExternalSlotProps = {
  input?: Partial<OutlinedInputProps>;
  htmlInput?: InputBaseComponentProps;
  inputLabel?: { shrink?: boolean };
  formHelperText?: Partial<FormHelperTextProps>;
};

const TruentityTextField = ({
  id,
  label,
  min,
  max,
  step,
  maxLength,
  defaultValue,
  shrink,
  autoFocus,
  icon,
  multiple,
  startIcon,
  InputProps,
  value,
  masked,
  maskedVisibleChars,
  onFocus,
  onBlur,
  ref,
  inputRef,
  inputProps: inputPropsProp,
  FormHelperTextProps,
  slotProps: slotPropsProp,
  ...props
}: TruentityTextFieldProps) => {
  // MUI v9 Autocomplete wires its input ref/handlers through `params.slotProps` (input + htmlInput),
  // so those must be merged in rather than replaced, or the Autocomplete crashes on click (null inputRef).
  const externalSlotProps = (slotPropsProp ?? {}) as ExternalSlotProps;
  const { inputProps: legacyInputProps, ...inputSlotProps } = InputProps ?? {};
  const [isRevealed, setIsRevealed] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  const variant = props.variant || 'outlined';

  // Partial mask display (first N chars + dots): only when blurred and not revealed
  const showPartialMask = masked && (maskedVisibleChars ?? 0) > 0 && !isRevealed && !isFocused;

  const displayValue = (() => {
    if (!showPartialMask || !value || typeof value !== 'string') return value;
    const visible = value.slice(0, maskedVisibleChars);
    const dots = '•'.repeat(Math.max(0, value.length - maskedVisibleChars!));
    return visible + dots;
  })();

  const inputType = (() => {
    if (!masked || isRevealed) return props.type ?? 'text';
    // While focused (typing): let browser handle masking natively so dots don't corrupt the value
    if ((maskedVisibleChars ?? 0) > 0) return isFocused ? 'password' : 'text';
    return 'password';
  })();

  const handleFocus = (e: FocusEvent<HTMLInputElement>) => {
    if (masked && (maskedVisibleChars ?? 0) > 0) setIsFocused(true);
    onFocus?.(e);
  };

  const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
    if (masked && (maskedVisibleChars ?? 0) > 0) setIsFocused(false);
    onBlur?.(e);
  };

  const eyeAdornment = masked ? (
    <InputAdornment position="end">
      <Tooltip title={isRevealed ? 'Hide' : 'Reveal'}>
        <IconButton size="small" onClick={() => setIsRevealed(r => !r)} edge="end">
          {isRevealed ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
        </IconButton>
      </Tooltip>
    </InputAdornment>
  ) : undefined;

  const endAdornment = icon ? <InputAdornment position="start">{icon}</InputAdornment> : undefined;
  const startAdornment = startIcon ? <InputAdornment position="start">{startIcon}</InputAdornment> : undefined;

  if (value && typeof shrink === 'undefined') {
    shrink = true;
  }

  return (
    <MuiTextField
      id={id}
      label={label}
      defaultValue={defaultValue}
      fullWidth
      variant={variant}
      margin="dense"
      size="medium"
      autoFocus={autoFocus}
      {...props}
      value={displayValue}
      type={inputType}
      onFocus={handleFocus}
      onBlur={handleBlur}
      inputRef={inputRef ?? ref}
      slotProps={{
        ...externalSlotProps,
        input: {
          ...externalSlotProps.input,
          ...inputSlotProps,
          endAdornment: eyeAdornment ?? InputProps?.endAdornment ?? externalSlotProps.input?.endAdornment ?? endAdornment,
          startAdornment: InputProps?.startAdornment ?? externalSlotProps.input?.startAdornment ?? startAdornment
        },
        // In patients details/medications/new the medication anem didnt suggest the name like previously
        // Native <input> attributes go on htmlInput; a nested `input.inputProps` would override this slot wholesale.
        htmlInput: {
          min,
          max,
          step,
          maxLength,
          multiple,
          ...externalSlotProps.htmlInput,
          ...legacyInputProps,
          ...inputPropsProp
        },
        inputLabel: {
          ...externalSlotProps.inputLabel,
          shrink: shrink ?? externalSlotProps.inputLabel?.shrink
        },
        formHelperText: FormHelperTextProps ?? externalSlotProps.formHelperText
      }}
    />
  );
};

export default TruentityTextField;
